
import { t } from '@nextcloud/l10n'
import { provide } from 'vue'
import NcActionButton from '@nextcloud/vue/components/NcActionButton'
import NcActions from '@nextcloud/vue/components/NcActions'
import NcButton from '@nextcloud/vue/components/NcButton'
import NcDialog from '@nextcloud/vue/components/NcDialog'
import IconArrowLeft from 'vue-material-design-icons/ArrowLeft.vue'
import Delete from 'vue-material-design-icons/Delete.vue'
import DotsCircle from 'vue-material-design-icons/DotsCircle.vue'
import Reload from 'vue-material-design-icons/Reload.vue'
import BreakoutRoomItem from '../RightSidebar/BreakoutRooms/BreakoutRoomItem.vue'
import SelectableParticipant from './SelectableParticipant.vue'
import { ATTENDEE, CONVERSATION, PARTICIPANT } from '../../constants.ts'
import { useBreakoutRoomsStore } from '../../stores/breakoutRooms.ts'

export default {
	name: 'BreakoutRoomsParticipantsEditor',

	components: {
		NcActions,
		NcActionButton,
		DotsCircle,
		Reload,
		BreakoutRoomItem,
		SelectableParticipant,
		NcButton,
		IconArrowLeft,
		Delete,
		NcDialog,
	},

	props: {
		token: {
			type: String,
			required: true,
		},

		roomNumber: {
			type: Number,
			default: undefined,
		},

		breakoutRooms: {
			type: Array,
			default: () => [],
		},
	},

	emits: ['back', 'close'],

	setup() {
		// Add a visual bulk selection state for SelectableParticipant component
		provide('bulkParticipantsSelection', true)

		return {
			breakoutRoomsStore: useBreakoutRoomsStore(),
		}
	},

	data() {
		return {
			selectedParticipants: [],
			assignments: [],
			showDialog: false,
		}
	},

	computed: {
		participants() {
			return this.$store.getters.participantsList(this.token).filter((participant) => {
				return (participant.participantType === PARTICIPANT.TYPE.USER
					|| participant.participantType === PARTICIPANT.TYPE.GUEST)
				&& participant.actorType === ATTENDEE.ACTOR_TYPE.USERS
			})
		},

		attendeesById() {
			return this.$store.state.participantsStore.attendees[this.token]
		},

		unassignedParticipants() {
			if (this.assignments.length === 0) {
				return []
			}
			// Flatten assignments array
			const assignedParticipants = this.assignments.flat()
			return this.participants.filter((participant) => {
				return !assignedParticipants.includes(participant.attendeeId)
			})
		},

		hasSelected() {
			return this.selectedParticipants.length > 0
		},

		hasAssigned() {
			return this.assignments.flat().length > 0
		},

		// True if there's one or more unassigned participants
		hasUnassigned() {
			return this.unassignedParticipants.length > 0
		},

		// If the breakoutRooms prop is populated it means that this component is
		// being used to reorganize the attendees of an existing breakout room.
		isReorganizingAttendees() {
			return this.breakoutRooms.length
		},

		confirmButtonLabel() {
			return this.isReorganizingAttendees ? t('spreed', 'Confirm') : t('spreed', 'Create breakout rooms')
		},

		confirmButtonType() {
			return this.hasUnassigned ? 'secondary' : 'primary'
		},

		resetButtonLabel() {
			return t('spreed', 'Reset')
		},

		conversation() {
			return this.$store.getters.conversation(this.token)
		},

		breakoutRoomsConfigured() {
			return this.conversation.breakoutRoomMode !== CONVERSATION.BREAKOUT_ROOM_MODE.NOT_CONFIGURED
		},

		deleteButtonLabel() {
			return t('spreed', 'Delete breakout rooms')
		},

		dialogMessage() {
			return t('spreed', 'Current breakout rooms and settings will be lost')
		},
	},

	created() {
		this.initialiseAssignments()
	},

	methods: {
		t,
		/**
		 * Initialise the assignments array.
		 *
		 * @param {boolean} forceReset If true, the assignments array will be reset if the breakoutRooms prop is populated.
		 */
		initialiseAssignments(forceReset) {
			if (this.isReorganizingAttendees && !forceReset) {
				this.assignments = this.breakoutRooms.map((room) => {
					const participantInBreakoutRoomActorIdList = this.$store.getters.participantsList(room.token)
						.map((participant) => participant.actorId)

					return this.participants.filter((participant) => {
						return participantInBreakoutRoomActorIdList.includes(participant.actorId)
					}).map((participant) => participant.attendeeId)
				})
			} else {
				this.assignments = Array.from(Array(this.isReorganizingAttendees
					? this.breakoutRooms.length
					: this.roomNumber), () => [])
			}
		},

		assignAttendees(roomIndex) {
			this.selectedParticipants.forEach((attendeeId) => {
				if (this.unassignedParticipants.find((participant) => participant.attendeeId === attendeeId)) {
					this.assignments[roomIndex].push(attendeeId)
					return
				}

				const assignedRoomIndex = this.assignments.findIndex((room) => room.includes(attendeeId))

				if (assignedRoomIndex === roomIndex) {
					return
				}

				this.assignments[assignedRoomIndex].splice(this.assignments[assignedRoomIndex].findIndex((id) => id === attendeeId), 1)
				this.assignments[roomIndex].push(attendeeId)
			})

			this.selectedParticipants = []
		},

		roomName(index) {
			return this.breakoutRooms[index]?.displayName
				?? t('spreed', 'Room {roomNumber}', { roomNumber: index + 1 })
		},

		resetAssignments() {
			this.selectedParticipants = []
			this.assignments = []
			this.initialiseAssignments(true)
		},

		goBack() {
			this.$emit('back')
		},

		handleSubmit() {
			this.isReorganizingAttendees ? this.reorganizeAttendees() : this.createRooms()
		},

		createAttendeeMap() {
			const attendeeMap = {}
			this.assignments.forEach((room, index) => {
				room.forEach((attendeeId) => {
					attendeeMap[attendeeId] = index
				})
			})
			return JSON.stringify(attendeeMap)
		},

		createRooms() {
			this.breakoutRoomsStore.configureBreakoutRooms({
				token: this.token,
				mode: 2,
				amount: this.roomNumber,
				attendeeMap: this.createAttendeeMap(),
			})
			this.$emit('close')
		},

		reorganizeAttendees() {
			this.breakoutRoomsStore.reorganizeAttendees({
				token: this.token,
				attendeeMap: this.createAttendeeMap(),
			})
			this.$emit('close')
		},

		toggleShowDialog() {
			this.showDialog = !this.showDialog
		},

		deleteBreakoutRooms() {
			this.breakoutRoomsStore.deleteBreakoutRooms(this.token)
		},
	},
}
